import type { CityConfig } from "@/pages/city-template/types";

export const hartfordConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Hartford",
  state: "Connecticut",
  stateAbbr: "CT",
  slug: "hartford",
  route: "/hartford",
  title: "Expedited Passport Services in Hartford, CT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Hartford, CT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered State Department courier serving Hartford and all of Connecticut.",
  h1: "Expedited Passport Services in Hartford, CT",
  heroSubtitle:
    "Connecticut's capital city deserves capital-level service. Fast Passport Center delivers emergency and expedited passports as fast as 24 hours for Hartford travelers — no agency visit required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Golden%20hour%20skyline%20of%20Hartford%20Connecticut%20featuring%20the%20iconic%20State%20Capitol%20dome%20and%20the%20Connecticut%20River%2C%20warm%20amber%20lighting%20reflecting%20on%20the%20water%2C%20downtown%20cityscape%20with%20modern%20architecture%2C%20professional%20travel%20destination%20photography%2C%20clean%20modern%20composition%20with%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=ct-hart-hero-001&orientation=landscape",
  heroImageAlt: "Downtown Hartford Connecticut skyline at golden hour",
  geoPosition: "41.7658; -72.6734",
  geoPlacename: "Hartford, Connecticut, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Connecticut Passport Agency",
    address: "850 Canal Street, Stamford, CT 06902",
    distance: "~100 miles from Hartford",
  },
  nearbyCitiesZips: [
    { name: "Hartford", zips: "06101-06106, 06112, 06114, 06115, 06120, 06132, 06134, 06141, 06142, 06143, 06144, 06145, 06146, 06147, 06152, 06154, 06155, 06156, 06160, 06161, 06167, 06176, 06180, 06183, 06199" },
    { name: "West Hartford", zips: "06107, 06110, 06117, 06119, 06127, 06133, 06137" },
    { name: "East Hartford", zips: "06108, 06118, 06128, 06138" },
    { name: "New Britain", zips: "06050, 06051, 06052, 06053" },
    { name: "Manchester", zips: "06040, 06041, 06042, 06045" },
    { name: "Bloomfield", zips: "06002" },
    { name: "Windsor", zips: "06006, 06095" },
    { name: "Wethersfield", zips: "06109" },
    { name: "Rocky Hill", zips: "06067" },
    { name: "Glastonbury", zips: "06033" },
    { name: "South Windsor", zips: "06074" },
    { name: "Enfield", zips: "06082, 06083" },
    { name: "Southington", zips: "06489, 06479" },
    { name: "Bristol", zips: "06010, 06011" },
    { name: "Newington", zips: "06111" },
    { name: "Cromwell", zips: "06416" },
    { name: "Berlin", zips: "06037" },
    { name: "Farmington", zips: "06030, 06032, 06034" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Hartford travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving All of Hartford",
      desc: "Downtown, West Hartford, East Hartford, Asylum Hill, Parkville — we cover every Hartford neighborhood.",
    },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Hartford, CT",
      rating: 5,
      text: "I needed my passport in 5 days for a conference in Toronto. Fast Passport Center delivered it with a day to spare. Their Hartford team was incredibly responsive.",
      service: "Emergency Passport",
    },
    {
      name: "Robert M.",
      location: "West Hartford, CT",
      rating: 5,
      text: "Renewed my passport through Fast Passport Center after realizing it expired 3 months before my Europe trip. Had it back in under a week.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa T.",
      location: "Manchester, CT",
      rating: 5,
      text: "Applied for my son's first passport through Fast Passport Center. They helped me understand the two-parent consent rule and made it stress-free.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "hart-1",
      name: "Hartford Main Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "141 Weston Street",
      city: "Hartford",
      state: "CT",
      zip: "06120",
      phone: "860-714-6120",
      hours: "Mon-Fri 8:30 AM - 4:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown Hartford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "hart-2",
      name: "Hartford City Hall",
      type: "City Clerk / Passport Services",
      address: "550 Main Street",
      city: "Hartford",
      state: "CT",
      zip: "06103",
      phone: "860-757-9200",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      distance: "Downtown Hartford",
      website: "https://www.hartford.gov/",
      websiteLabel: "City of Hartford",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "hart-3",
      name: "West Hartford Town Hall",
      type: "Town Clerk / Passport Acceptance Facility",
      address: "50 South Main Street",
      city: "West Hartford",
      state: "CT",
      zip: "06107",
      phone: "860-561-7500",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "West Hartford",
      website: "https://www.westhartfordct.gov/",
      websiteLabel: "West Hartford Town Hall",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Hartford, CT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Connecticut Passport Agency if I live in Hartford?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Connecticut Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Hartford?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Hartford and West Hartford. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Hartford?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Hartford?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Hartford?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Hartford. We serve Hartford and all of Connecticut through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Hartford", address: " various CVS locations in Hartford", hours: "Mon-Sun, hours vary by location", website: "https://www.cvs.com/store-locator/cvs-pharmacy-locations" },
    { name: "Walgreens — Hartford", address: "various Walgreens locations in Hartford", hours: "Mon-Sun, hours vary by location", website: "https://www.walgreens.com/store-locator" },
    { name: "UPS Store — Hartford", address: "various UPS Store locations in Hartford", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM", website: "https://www.theupsstore.com/tools/find-a-store" },
    { name: "FedEx Office — Hartford", address: "various FedEx Office locations in Hartford", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM", website: "https://local.fedex.com/" },
    { name: "Walmart — Hartford", address: "various Walmart locations in Hartford", hours: "Mon-Sun 7:00 AM - 10:00 PM", website: "https://www.walmart.com/store/finder" },
  ],
  dmvLocations: [
    {
      name: "CT DMV — Wethersfield",
      address: "60 State Street, Wethersfield, CT 06109",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Connecticut DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: true,
    },
    {
      name: "CT DMV — Manchester",
      address: "303 Middle Turnpike W, Manchester, CT 06040",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "~14 miles from Hartford",
      address: "Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's primary airport serving Hartford and all of New England. Offers domestic flights and international connections to Canada, Ireland, and the Caribbean.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "Bradley International",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~100 miles from Hartford",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and worldwide destinations. Hartford travelers often use Logan for extensive international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "JFK",
      name: "JFK International Airport",
      distance: "~100 miles from Hartford",
      address: "JFK Access Rd, Queens, NY 11430",
      note: "One of the busiest international airports in the world with flights to virtually every global destination. Amtrak and bus connections available from Hartford.",
      flightUrl: "https://www.jfkairport.com/",
      flightLabel: "JFK International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Hartford travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Hartford residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Hartford residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Hartford residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Hartford, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Hartford residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Hartford travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Hartford residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Hartford residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Hartford residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Hartford." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Hartford." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Hartford travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Hartford travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Hartford-area team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Hartford address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: [
        "Mail-in application",
        "No expedited handling",
        "No dedicated agent",
        "No phone or email contact for your case",
        "Updates only by physical mail letter",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: [
        "Secure overnight shipping included",
        "Document review before submission",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: [
        "For documented life-or-death emergencies only",
        "Requires proof of emergency travel",
        "Strictly for documented life-or-death emergencies",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Hartford-Specific Guidance", agency: "Generic — no capital region, insurance industry, or Connecticut expertise", fpc: "Full Hartford/capital region/Connecticut knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Hartford", route: "/hartford" },
    { name: "New Haven", route: "/new-haven" },
    { name: "Stamford", route: "/stamford" },
    { name: "Bridgeport", route: "/bridgeport" },
    { name: "Waterbury", route: "/waterbury" },
    { name: "Greenwich", route: "/greenwich" },
  ],
  fpcOfficeNote:
    "Fast Passport Center serves Hartford and all of Connecticut as a remote courier service. We do not have a physical office in Hartford, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "850 Canal Street, Stamford, CT 06902",
  fpcOfficeDistance: "Remote courier service — Hartford, CT area",
  serviceAreaIntro:
    "Fast Passport Center provides expedited passport services to all Hartford neighborhoods and surrounding Connecticut communities. From downtown Hartford to West Hartford, East Hartford to Asylum Hill — we serve every Hartford neighborhood with the same fast, reliable processing.",
  nearbyCitiesIntro:
    "We proudly serve Hartford and all surrounding Connecticut communities. Whether you're in the capital district or a nearby suburb, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro:
    "Our Hartford customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro:
    "These are the most common mistakes Hartford travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro:
    "Hartford and Connecticut travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro:
    "Common questions about expedited passport services in Hartford, CT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/connecticut",
  statePageName: "Connecticut Passport Services",
  dmvWebsite: "https://portal.ct.gov/DMV",
  dmvLabel: "Connecticut DMV — Official Website",
  vitalRecordsLinks: [
    { icon: "ri-user-heart-line", label: "Birth Certificates — Connecticut DPH", desc: "Request certified Connecticut birth certificates from the Department of Public Health Vital Records Office. Available by mail, online, or in person at the Hartford office.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-file-history-line", label: "Death Certificates — Connecticut DPH", desc: "Obtain certified Connecticut death certificates through the DPH Vital Records Office. Mail, online, and in-person options available.", url: "https://portal.ct.gov/DPH/Vital-Records/Vital-Records-Home" },
    { icon: "ri-heart-pulse-line", label: "Marriage Certificates — Hartford Town Clerk", desc: "Request certified marriage certificates from the Hartford Town Clerk's Office. Marriage records are maintained by the town where the ceremony took place.", url: "https://www.hartford.gov/town-clerk" },
    { icon: "ri-scales-line", label: "Divorce Records — Hartford Superior Court", desc: "Obtain certified divorce decrees from the Hartford Judicial District Superior Court. Divorce records are maintained by the court where the decree was granted.", url: "https://jud.ct.gov/" },
  ],
};

export default hartfordConfig;